/**
 * supercluster によるピンのクラスタリングと Marker 管理。
 *
 * design §16-4 / 要件 7-5, 7-6 に対応。
 * - 近接ピンを束ねて件数バッジで表示、ズームで展開
 * - 単独ピンは buildPinElement のアバター＋鮮度で表示
 *
 * 描画方針（案Z・DOM 最適化）:
 * - Marker はビューポートに関係なく全件を常時描画する（〜数十人規模では DOM でも軽い）。
 *   これにより flyTo/pan の直後も移動先のピンが即表示され、moveend 待ちの遅延が無い。
 * - Marker は setLngLat した座標に固定され、地図の pan/zoom/flyTo に自動追従する。
 * - クラスタ構成はズームレベルが変わったときだけ再計算する（pan では再構築しない＝軽い）。
 *
 * 将来 100 人超で重くなった場合は、本クラスの内部だけを MapLibre シンボルレイヤー
 * （WebGL）へ差し替える（setUsers/destroy のインターフェースは維持）。
 */
import { Marker, type Map as MaplibreMap } from 'maplibre-gl';
import Supercluster from 'supercluster';
import type { UserLocation } from '../api/client';
import type { Lang } from '../i18n/lang';
import { buildPinElement, freshnessOpacityOf } from './pinStyle';

type PointProps = { user: UserLocation };

/** クラスタ点のプロパティ（supercluster が付与する形）。 */
interface ClusterProps {
  cluster: true;
  cluster_id: number;
  point_count: number;
}

function isCluster(props: PointProps | ClusterProps): props is ClusterProps {
  return (props as ClusterProps).cluster === true;
}

/** 全世界を覆う bbox（ビューポート制限をしないため）。 */
const WORLD_BBOX: [number, number, number, number] = [-180, -85, 180, 85];

export interface PinClusterOptions {
  /** 自分の user_id（ピン強調用・任意）。 */
  readonly selfUserId?: string;
  /** 参加者ピンがクリックされたとき（軌跡強調の選択・任意）。 */
  readonly onSelectUser?: (userId: string) => void;
}

export class PinCluster {
  private readonly map: MaplibreMap;
  /** 自分の user_id（ログイン・ログアウトで変わるので後から差し替えられる・タスク51）。 */
  private selfUserId?: string;
  private readonly onSelectUser?: (userId: string) => void;
  private index: Supercluster<PointProps> | null = null;
  private serverTime = 0;
  private markers = new Map<string, Marker>();
  /** 最後に描画したズーム（整数）。ズームが変わったときだけ再クラスタする。 */
  private lastZoom: number | null = null;
  private readonly onZoom: () => void;
  /** ピンの「N分前」文言の言語。 */
  private lang: Lang = 'ja';

  constructor(map: MaplibreMap, opts: PinClusterOptions = {}) {
    this.map = map;
    this.selfUserId = opts.selfUserId;
    this.onSelectUser = opts.onSelectUser;
    // ズーム変化時のみ再クラスタ（pan では Marker が自動追従するので再構築不要）。
    this.onZoom = () => {
      const z = Math.round(this.map.getZoom());
      if (z !== this.lastZoom) {
        this.render();
      }
    };
    this.map.on('zoomend', this.onZoom);
  }

  /** ピン文言の言語を設定する（lang 変更時は呼び出し側で再描画する）。 */
  setLang(lang: Lang): void {
    this.lang = lang;
  }

  /**
   * 自分の user_id を差し替えて再描画する（自分のピンにオレンジの縁・タスク51）。
   * 地図の読み込み後にログイン・ログアウトしても反映されるようにする。
   */
  setSelfUserId(userId: string | null): void {
    const next = userId ?? undefined;
    if (next === this.selfUserId) {
      return;
    }
    this.selfUserId = next;
    this.lastZoom = null;
    this.render();
  }

  /** ポーリング結果でクラスタ index を作り直し、再描画する。 */
  setUsers(users: UserLocation[], serverTime: number): void {
    this.serverTime = serverTime;
    this.index = new Supercluster<PointProps>({
      radius: 60,
      maxZoom: 18,
    });
    this.index.load(
      users.map((user) => ({
        type: 'Feature',
        properties: { user },
        geometry: {
          type: 'Point',
          coordinates: [user.latest.lng, user.latest.lat],
        },
      })),
    );
    // データ更新時は無条件で再描画
    this.lastZoom = null;
    this.render();
  }

  /**
   * 指定ズーム（省略時は現在ズーム）で即座に再クラスタ・再描画する。
   * アコーディオンからの移動時に、移動アニメーション完了（zoomend）を待たず
   * 目的ズームでの個別ピンを先に出すために使う。
   */
  renderAtZoom(zoom: number): void {
    this.render(Math.round(zoom));
  }

  /**
   * 全ピン/クラスタを描画する（ビューポート制限なし）。
   * Marker は地図の移動に自動追従するため、pan では呼ばない。
   * @param zoomOverride この値のズームでクラスタリングする（省略時は現在ズーム）
   */
  private render(zoomOverride?: number): void {
    if (!this.index) {
      return;
    }
    const zoom = zoomOverride ?? Math.round(this.map.getZoom());
    this.lastZoom = zoom;
    // ビューポートではなく全世界を対象にして全ピンを描画する
    const clusters = this.index.getClusters(WORLD_BBOX, zoom);

    const nextIds = new Set<string>();

    for (const c of clusters) {
      const [lng, lat] = c.geometry.coordinates;
      const props = c.properties as PointProps | ClusterProps;

      if (isCluster(props)) {
        const id = `cluster-${props.cluster_id}`;
        nextIds.add(id);
        // クラスタは件数のみ。既存があれば作り直さない（同一ズーム内では不変）
        if (!this.markers.has(id)) {
          const el = this.buildClusterElement(props.point_count, () =>
            this.expandCluster(props.cluster_id, lng, lat),
          );
          this.markers.set(
            id,
            new Marker({ element: el }).setLngLat([lng, lat]).addTo(this.map),
          );
        }
      } else {
        const user = props.user;
        const id = `user-${user.user_id}`;
        nextIds.add(id);
        // ユーザーピンは鮮度が変わるので作り直す
        this.markers.get(id)?.remove();
        const el = buildPinElement({
          user,
          serverTime: this.serverTime,
          isSelf: user.user_id === this.selfUserId,
          lang: this.lang,
        });
        // ピンクリックで当該ユーザーを選択（軌跡強調）。アコーディオン選択と同じ効果。
        // 地図の click（空白クリック=選択解除）へ伝播しないよう stopPropagation する。
        if (this.onSelectUser) {
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            this.onSelectUser!(user.user_id);
          });
        }
        // 鮮度による不透明度は MapLibre 経由で設定する（要素の style.opacity は
        // Marker が内部管理・上書きするため、setOpacity を使う必要がある）。
        const ageSec = Math.max(0, this.serverTime - user.latest.updated_at);
        const marker = new Marker({ element: el }).setLngLat([lng, lat]);
        marker.setOpacity(String(freshnessOpacityOf(ageSec)));
        marker.addTo(this.map);
        this.markers.set(id, marker);
      }
    }

    // 不要になった Marker を除去
    for (const [id, marker] of this.markers) {
      if (!nextIds.has(id)) {
        marker.remove();
        this.markers.delete(id);
      }
    }
  }

  /** クラスタ件数バッジの要素を作る。 */
  private buildClusterElement(count: number, onClick: () => void): HTMLElement {
    const el = document.createElement('div');
    el.className = 'cluster';
    el.textContent = String(count);
    el.addEventListener('click', onClick);
    return el;
  }

  /** クラスタをクリックしたら展開ズームへ。 */
  private expandCluster(clusterId: number, lng: number, lat: number): void {
    if (!this.index) {
      return;
    }
    const zoom = this.index.getClusterExpansionZoom(clusterId);
    this.map.easeTo({ center: [lng, lat], zoom });
  }

  /** クリーンアップ。 */
  destroy(): void {
    this.map.off('zoomend', this.onZoom);
    for (const marker of this.markers.values()) {
      marker.remove();
    }
    this.markers.clear();
  }
}
